import { useMemo } from 'react';
import { getArraySort } from '@/pages/intelligence/utils/commonUtils';
import { buildTooltipFormatter } from '../../utils/common';

interface useDataPropsTypes extends StackBarEchartsPropsTypes {
  isCN: boolean;
  echartsContainerHeight?: number; // 当前echarts容器echarts占的高度，用于计算每个echart的高度
  tooltipHeaderRightTitle?: string;
  seriesNameFormatter?: (seriesName: string) => string;
}

const DEFAULT_PROPS: useDataPropsTypes = {
  isCN: false,
  data: null,
  ignoreSortNames: [],
  tooltipPosition: 'right',
  tooltipHeaderRightTitle: '',
  echartsContainerHeight: 80,
  fontSize: 12,
};

const useData = (props: useDataPropsTypes) => {
  const {
    isCN,
    data,
    ignoreSortNames,
    tooltipPosition,
    tooltipHeaderRightTitle,
    seriesNameFormatter,
    echartsContainerHeight,
    fontSize,
  } = props;

  const isNoData = useMemo(
    () =>
      !Array.isArray(data) ||
      !data?.length ||
      data?.every((t) => !Array.isArray(t?.data) || !t?.data?.length),
    [data],
  );

  // 对数据进行降序排序及计算最大的加总数据
  const { sortData } = useMemo(() => {
    if (!Array.isArray(data) || !data?.length) {
      return { sortData: [] };
    }
    const newData = [];
    for (let i = 0; i < data?.length; i++) {
      const t = data?.[i] || {
        theme: '',
        label: '',
        data: [],
      };
      const itemData = t?.data;
      let newItemData: {
        name: string;
        value: string | number;
        [keyName: string]: any;
      }[] = [];
      let total = 0;
      // 降序排序及计算total
      if (Array.isArray(itemData) && itemData?.length) {
        if (Array.isArray(ignoreSortNames) && ignoreSortNames?.length) {
          const ignoreSortData = [];
          const needSortData = [];
          for (let j = 0; j < itemData?.length; j++) {
            const k = itemData?.[j];
            const name = itemData?.[j]?.name;
            if (ignoreSortNames?.includes(name || '')) {
              ignoreSortData.push(k);
            } else {
              needSortData.push(k);
            }
          }
          const sortData = getArraySort(needSortData, 'number', 'desc', 'value');
          newItemData = [...sortData, ...ignoreSortData];
        } else {
          newItemData = getArraySort(itemData, 'number', 'desc', 'value');
        }
        total = itemData?.reduce((o, t) => {
          o = o + (Number(t?.value) || 0);
          return o;
        }, 0);
      }
      newData.push({ ...t, total, data: newItemData });
    }
    return {
      sortData: newData,
    };
  }, [data, ignoreSortNames]);

  const resultList = useMemo(() => {
    const result = [];
    const itemEchartHeight = (echartsContainerHeight as number) / (sortData?.length || 1);
    const baseOptions = {
      textStyle: {
        fontSize,
      },
      grid: {
        x: '-0.5px', // 柱子间边框（间距）导致要左移一点
        y: '0%',
        x2: '0%',
        y2: '0%',
        containLabel: true,
      },
      tooltip: {
        // show: true,
        // showContent: true,
        // alwaysShowContent: true,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        className: 'dbi-common-echarts-tooltip-primary-styles',
        trigger: 'axis',
        showDelay: 0,
        hideDelay: 0,
        transitionDuration: 0.2,
        enterable: false,
        position(point: [number, number], params: any, dom: any, rect: any, size: any) {
          // 浮窗位置
          const { dataIndex } = params;
          const { contentSize } = size;
          const verticalOffset = -(contentSize?.[1] / 2);
          const position =
            tooltipPosition === 'left'
              ? [point?.[0] - contentSize?.[0] - 20, point?.[1] + verticalOffset]
              : [point?.[0] + 20, point?.[1] + verticalOffset];
          return position;
        },
        axisPointer: {
          type: 'line',
          // shadowStyle: {
          //   color: 'rgba(216, 218, 228, 0.5)',
          //   // color: 'rgba(187, 187, 187, 0.2)',
          // },
          lineStyle: {
            color: 'rgba(216, 218, 228, 0.5)',
            type: 'solid',
            width: itemEchartHeight,
          },
        },
        textStyle: {
          fontSize,
        },
        formatter: buildTooltipFormatter({
          isCN,
          headerRightTitle: tooltipHeaderRightTitle,
          seriesNameFormatter,
        }),
      },
      xAxis: {
        show: false,
        type: 'value',
        max: (value: { min: number; max: number }) => {
          // totalMax扩大12%（这个比例自己调整，能完整显示label内容就行），确保label能展示出来
          return value.max * 1.2;
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
      },
    };
    for (let i = 0; i < sortData?.length; i++) {
      const t = sortData?.[i];
      const series = [];
      for (let j = 0; j < t?.data?.length; j++) {
        const k = t?.data?.[j];
        series.push({
          name: k?.name,
          type: 'bar',
          stack: 'total',
          barMaxWidth: itemEchartHeight / 2 + 1,
          itemStyle: {
            color: k?.color,
            borderColor: '#fff',
            borderWidth: 0.5,
            borderType: 'solid',
          },
          data: [k],
          label: {
            show: j === t?.data?.length - 1,
            position: 'right',
            color: '#9B9FB0',
            // offset: [-4, 0], // 14px字号标签文案会被遮挡
            offset: [5, 1],
            formatter: t?.label,
          },
          /* markPoint: {
            symbol: 'pin',
            symbolOffset: ['-100%', '-60%'],
            label: {
              position: 'inside',
              formatter: 'Direct',
            },
            itemStyle: {
              color: 'red',
            },
            data: [
              { type: 'max', name: 'Max' },
              { type: 'min', name: 'Min' }
            ],
          }, */
        });
      }
      result.push({
        ...t,
        options: {
          ...baseOptions,
          yAxis: {
            show: false,
            type: 'category',
            data: [isCN && t?.themeCN ? t?.themeCN : t?.theme],
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
          },
          series,
        },
      });
    }

    return {
      height: itemEchartHeight,
      data: result,
    };
  }, [
    isCN,
    sortData,
    tooltipPosition,
    echartsContainerHeight,
    tooltipHeaderRightTitle,
    seriesNameFormatter,
  ]);

  return {
    ...resultList,
    isNoData,
  };
};

useData.defaultProps = DEFAULT_PROPS;

export default useData;
